import React, { useState, useContext } from 'react';
import { useTranslate } from 'react-translate';

import "./DownloadAllButton.css"

import { StatusIcon } from "./StatusIcon.js";

import { downloadInstances } from "./Downloader/Download.js";

/* Import icons */

import downloadIcon from "./Resources/Icons/download.svg";
import downloadingIcon from "./Resources/Icons/loading.svg";
import downloadCompleteIcon from "./Resources/Icons/download_complete.svg";
import downloadFailIcon from "./Resources/Icons/download_fail.svg";

import successIcon from "./../Notifications/Resources/Icons/success.svg";
import failureIcon from "./../Notifications/Resources/Icons/info.svg";

/* Import Notification Builder / Context */

import { NotificationBuilder } from '../Notifications/NotificationBuilder';
import { NotificationContext } from "./../Notifications/NotificationContext.js";

import { ReportStatus } from "./Downloader/Reports/Report.js";

/* ==----------------------------------------------------------------------------------------------------------== */
/* ==----------------------------------------== Global definitions ==------------------------------------------== */
/* ==----------------------------------------------------------------------------------------------------------== */

/* Used to switch the button's state */
const DownloadState = {
    WAITING: 0,
    DOWNLOADING: 1,
    COMPLETED: 2,
    ERROR: 3,
};

/* ==----------------------------------------------------------------------------------------------------------== */
/* ==------------------------------------------== React component ==-------------------------------------------== */
/* ==----------------------------------------------------------------------------------------------------------== */

export function DownloadAllButton(props) {

    const t = useTranslate("buttons");

    const nt = useTranslate("notificationTitles");
    const nd = useTranslate("notificationDescriptions");

    const [data, setState] = useState(
        {
            state: DownloadState.WAITING,
            text: t("downloadAll"),
            icon: downloadIcon,
            className: "standard-icon",
            timeout: null
        }
    );

    const launchNotification = useContext(NotificationContext).addNotification;

    /**
     * @description Small function that makes it easier to update the state
     * @param { DownloadState } state
     * @param { String } text
     * @param { Icon } icon
     * @param { String } className
     * @param { Timeout } timeout Timer that resets the state to DownloadState.WAITING after download
     */
    function updateState(state, text, icon, className, timeout = null) {
        setState(
            {
                state: state,
                text: text,
                icon: icon,
                className: className,
                timeout: timeout
            }
        );
    }

    /**
     * @description Button click event, handles differently depending on the button state
     * */
    async function handleClick() {
        try {
            switch (data.state) {
                /* Waiting for download */
                case DownloadState.WAITING:
                    updateState(DownloadState.DOWNLOADING, t("downloading"), downloadingIcon, "spinning-icon");

                    let downloadReport = await downloadInstances(props.instances, props.linkedInstances, props.projectName + ".zip");

                    let descKey = null;
                    let notification = null;

                    /* Retrieve Download status and inform the user */
                    switch (downloadReport.status) {
                        case ReportStatus.OK:   /* [ Success ] */
                            descKey = downloadReport.data > 1 ? "downloadManyComplete" : "downloadSingleComplete";
                            notification = new NotificationBuilder(successIcon, nt("downloadComplete"), nd(descKey), "notification-success");

                            launchNotification(notification);

                            updateState(DownloadState.COMPLETED, t("downloadComplete"), downloadCompleteIcon, "standard-icon");

                            break;
                        case ReportStatus.TIMEOUT:   /* [ Timeout error ] */
                            notification = new NotificationBuilder(failureIcon, nt("downloadTimeoutError"), nd("downloadTimeoutError"), "notification-error");

                            launchNotification(notification);

                            updateState(DownloadState.ERROR, t("downloadError"), downloadFailIcon, "error-icon");

                            break;
                        default:    /* [ Any other error ] */
                            descKey = downloadReport.data > 0 ? "downloadManyError" : "downloadSingleError";
                            notification = new NotificationBuilder(failureIcon, nt("downloadError"), nd(descKey), "notification-error");

                            launchNotification(notification);

                            updateState(DownloadState.ERROR, t("downloadError"), downloadFailIcon, "error-icon");
                    }

                    clearTimeout(data.timeout);

                    let timeout = setTimeout(() => updateState(DownloadState.WAITING, t("downloadAll"), downloadIcon, "standard-icon", timeout), 10000);

                    break;
                /* Currently downloading */
                case DownloadState.DOWNLOADING:
                    break;
                /* Finished downloading, one way or the other */
                default:
                    clearTimeout(data.timeout);

                    updateState(DownloadState.WAITING, t("downloadAll"), downloadIcon, "standard-icon", null);
            }
        } catch (error) {
            /* Reset button state, launch notification and set state change timer */

            console.log(error);

            updateState(DownloadState.ERROR, t("downloadError"), downloadFailIcon, "error-icon");

            let notification = new NotificationBuilder(failureIcon, nt("downloadError"), nd("downloadCrashError"), "notification-error");

            launchNotification(notification);

            clearTimeout(data.timeout);

            let timeout = setTimeout(() => updateState(DownloadState.WAITING, t("downloadAll"), downloadIcon, "standard-icon", timeout), 10000);
        }
    }

    return (
        <button title={t('download')} className={"frame"} onClick={() => handleClick()}>
            <StatusIcon className={data.className} icon={data.icon} width="24" height="24" />
            <span id="label" className="button-text">{data.text}</span>
        </button>
    );
}
